import { create } from "zustand";
import type { User as FirebaseUser } from "firebase/auth";

/**
 * sessionStorage key for an in-progress admin impersonation. Scoped to the
 * tab so a reload keeps the "view as" context, but a fresh tab starts clean
 * and closing the tab ends it. Only honoured when the signed-in user is an
 * admin (see `useImpersonation`), so a stale value can never leak data —
 * Firestore rules block non-admin reads of other users' weddings regardless.
 */
const IMPERSONATION_STORAGE_KEY = "aw:impersonate-uid";

function readStoredImpersonation(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistImpersonation(uid: string | null): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (uid) sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, uid);
    else sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    /* private mode / disabled storage — impersonation just won't persist */
  }
}

export interface AuthState {
  firebaseUser: FirebaseUser | null;
  initialized: boolean;
  /** Whether the signed-in user is on the platform admin allowlist. */
  isAdmin: boolean;
  /** Whether the admin-allowlist lookup has finished (gates admin routes). */
  adminResolved: boolean;
  /**
   * uid of the user an admin is currently viewing the app as (read-only), or
   * null when not impersonating. Gate every consumer on `isAdmin` too.
   */
  impersonatedUserId: string | null;
  setFirebaseUser: (u: FirebaseUser | null) => void;
  setInitialized: (b: boolean) => void;
  setIsAdmin: (b: boolean) => void;
  setAdminResolved: (b: boolean) => void;
  setImpersonatedUserId: (uid: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  initialized: false,
  isAdmin: false,
  adminResolved: false,
  impersonatedUserId: readStoredImpersonation(),
  setFirebaseUser: (firebaseUser) => set({ firebaseUser }),
  setInitialized: (initialized) => set({ initialized }),
  setIsAdmin: (isAdmin) => set({ isAdmin }),
  setAdminResolved: (adminResolved) => set({ adminResolved }),
  setImpersonatedUserId: (impersonatedUserId) => {
    persistImpersonation(impersonatedUserId);
    set({ impersonatedUserId });
  },
}));

/**
 * True when an admin is viewing another user's data read-only ("view as").
 * Non-hook accessor for module-level code (Firestore subscriptions). Background
 * reconciliation writes MUST be skipped in this mode — admins only have read
 * access to another couple's wedding, so the write would fail at the rules
 * layer as an unhandled permission-denied rejection.
 */
export function isImpersonationActive(): boolean {
  const { isAdmin, impersonatedUserId } = useAuthStore.getState();
  return isAdmin && impersonatedUserId != null;
}
